import { KeyboardEvent, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { all_routes } from "../../../feature-module/router/all_routes"
import { logout } from "../../../service/auth"
import {
  setExpandMenu,
  setMobileSidebar,
  toggleMiniSidebar,
} from "../../data/redux/sidebarSlice"
import { resetUser, selectUser } from "../../data/redux/slice/user"
import { setDataLayout, setDataTheme } from "../../data/redux/themeSettingSlice"
import ImageWithBasePath from "../imageWithBasePath"
import {
  selectCurrentSession,
  setCurrentSession,
  selectSessions,
} from "../../data/redux/slice/sessionsSlice"
import {
  selectBranches,
  selectCurrentBranch,
  setCurrentBranch,
} from "../../data/redux/slice/branchesSlice"
import { getProfile , UserProfile } from "../../../service/profile"
import { filterSidebarByPermissions } from "../../../utils"
import { canViewActivityLog, isSuperAdmin, isTeacher } from "../../../utils/rbacHelper"

interface Session {
  _id: string
  title: string
  startDate: string
  endDate: string
  isActive: boolean
  createdAt?: string
  updatedAt?: string
}

type MenuSearchResult = {
  label: string
  link: string
  icon?: string
  path: string[]
  searchText: string
}

const menuSearchIconFallbacks: Record<string, string> = {
  "Cashier Report": "ti ti-report-money",
  "Defaulters List": "ti ti-file-invoice",
  "Fee Types": "ti ti-receipt-tax",
  "Email Templates": "ti ti-mail",
  "Student Promotion": "ti ti-user-up",
}

const flattenMenuSearchItems = (
  items: any[],
  parentPath: string[] = [],
  parentIcon?: string
): MenuSearchResult[] =>
  items.flatMap(item => {
    const path = [...parentPath, item.label]
    const icon = item.icon || menuSearchIconFallbacks[item.label] || parentIcon
    const children = item.submenuItems
      ? flattenMenuSearchItems(item.submenuItems, path, icon)
      : []

    const isPageLink = item.link && item.link !== "#"
    const current = isPageLink
      ? [
          {
            label: item.label,
            link: item.link,
            icon,
            path,
            searchText: path.join(" ").toLowerCase(),
          },
        ]
      : []

    return [...current, ...children]
  })

const Header = () => {
  const routes = all_routes
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const dataTheme = useSelector((state: any) => state.themeSetting.dataTheme)
  const dataLayout = useSelector((state: any) => state.themeSetting.dataLayout)
  const User = useSelector(selectUser)
  const [profile, setProfile] = useState<UserProfile | null>(null)
  // A presigned avatar URL can go dead mid-session (link expired, transient
  // S3 error) — fall back to initials instead of a broken-image glyph with
  // no recovery. Campus logos get the same treatment via ImageWithBasePath's
  // own built-in fallback, so they don't need separate state here.
  const [avatarFailed, setAvatarFailed] = useState(false)
  const currentSession = useSelector(selectCurrentSession) as Session | null
  const sessions = useSelector(selectSessions)
  const branches = useSelector(selectBranches)
  const currentBranch = useSelector(selectCurrentBranch)

  const [isFullscreen, setIsFullscreen] = useState(false)
  const [menuSearch, setMenuSearch] = useState("")
  const [isMenuSearchFocused, setIsMenuSearchFocused] = useState(false)
  const [activeMenuResultIndex, setActiveMenuResultIndex] = useState(0)
  const [bookmarkedMenuLinks, setBookmarkedMenuLinks] = useState<string[]>([])
  const menuSearchRef = useRef<HTMLDivElement | null>(null)
  const menuSearchInputRef = useRef<HTMLInputElement | null>(null)

  const mobileSidebar = useSelector(
    (state: any) => state.sidebarSlice.mobileSidebar
  )

  const toggleMobileSidebar = () => {
    dispatch(setMobileSidebar(!mobileSidebar))
  }

  const onMouseEnter = () => {
    dispatch(setExpandMenu(true))
  }

  const onMouseLeave = () => {
    dispatch(setExpandMenu(false))
  }

  const handleToggleMiniSidebar = () => {
    if (dataLayout === "mini_layout") {
      dispatch(setDataLayout("default_layout"))
      localStorage.setItem("dataLayout", "default_layout")
    } else {
      dispatch(toggleMiniSidebar())
    }
  }

  const handleToggleClick = () => {
    if (dataTheme === "default_data_theme") {
      dispatch(setDataTheme("dark_data_theme"))
    } else {
      dispatch(setDataTheme("default_data_theme"))
    }
  }

  const location = useLocation()

  const searchableMenus = useMemo(
    () =>
      flattenMenuSearchItems(
        filterSidebarByPermissions(
          User?.permissions,
          isSuperAdmin(User),
          canViewActivityLog(User),
          isTeacher(User)
        )
      ),
    [User]
  )

  const menuBookmarkStorageKey = useMemo(() => {
    const userKey =
      User?._id ||
      User?.email ||
      User?.role?.name ||
      "anonymous"
    return `mpa-menu-bookmarks:${userKey}`
  }, [User])

  const bookmarkedMenus = useMemo(
    () =>
      bookmarkedMenuLinks
        .map(link => searchableMenus.find(item => item.link === link))
        .filter((item): item is MenuSearchResult => Boolean(item)),
    [bookmarkedMenuLinks, searchableMenus]
  )

  const menuSearchResults = useMemo(() => {
    const query = menuSearch.trim().toLowerCase()
    if (!query) return []

    return searchableMenus
      .filter(item => item.searchText.includes(query))
      .sort((a, b) => {
        const aLabel = a.label.toLowerCase()
        const bLabel = b.label.toLowerCase()
        const aPath = a.path.join(" ").toLowerCase()
        const bPath = b.path.join(" ").toLowerCase()
        const aScore = aLabel.startsWith(query)
          ? 0
          : aPath.startsWith(query)
            ? 1
            : 2
        const bScore = bLabel.startsWith(query)
          ? 0
          : bPath.startsWith(query)
            ? 1
            : 2
        return aScore - bScore || a.path.join(" > ").localeCompare(b.path.join(" > "))
      })
      .slice(0, 8)
  }, [menuSearch, searchableMenus])

  const isMenuSearchOpen =
    isMenuSearchFocused && menuSearch.trim().length > 0

  const resetMenuSearch = useCallback(() => {
    setMenuSearch("")
    setIsMenuSearchFocused(false)
    setActiveMenuResultIndex(0)
    menuSearchInputRef.current?.blur()
  }, [])

  useEffect(() => {
    setActiveMenuResultIndex(0)
  }, [menuSearch])

  useEffect(() => {
    const handleClickOutside = (event: globalThis.MouseEvent) => {
      if (
        menuSearchRef.current &&
        !menuSearchRef.current.contains(event.target as Node)
      ) {
        resetMenuSearch()
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [resetMenuSearch])

  useEffect(() => {
    try {
      const storedLinks = JSON.parse(
        localStorage.getItem(menuBookmarkStorageKey) || "[]"
      )
      setBookmarkedMenuLinks(
        Array.isArray(storedLinks)
          ? storedLinks.filter(link => typeof link === "string")
          : []
      )
    } catch {
      setBookmarkedMenuLinks([])
    }
  }, [menuBookmarkStorageKey])

  const openMenuSearchResult = (item: MenuSearchResult) => {
    resetMenuSearch()
    navigate(item.link)
  }

  useEffect(() => {
    resetMenuSearch()
  }, [location.pathname, resetMenuSearch])

  const isMenuBookmarked = (item: MenuSearchResult) =>
    bookmarkedMenuLinks.includes(item.link)

  const toggleMenuBookmark = (item: MenuSearchResult) => {
    setBookmarkedMenuLinks(current => {
      const nextLinks = current.includes(item.link)
        ? current.filter(link => link !== item.link)
        : [...current, item.link]

      localStorage.setItem(menuBookmarkStorageKey, JSON.stringify(nextLinks))
      return nextLinks
    })
  }

  const removeMenuBookmark = (link: string) => {
    setBookmarkedMenuLinks(current => {
      const nextLinks = current.filter(itemLink => itemLink !== link)
      localStorage.setItem(menuBookmarkStorageKey, JSON.stringify(nextLinks))
      return nextLinks
    })
  }

  const handleMenuSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!isMenuSearchOpen) return

    if (event.key === "ArrowDown") {
      event.preventDefault()
      setActiveMenuResultIndex(current =>
        Math.min(current + 1, menuSearchResults.length - 1)
      )
    }

    if (event.key === "ArrowUp") {
      event.preventDefault()
      setActiveMenuResultIndex(current => Math.max(current - 1, 0))
    }

    if (event.key === "Enter" && menuSearchResults[activeMenuResultIndex]) {
      event.preventDefault()
      openMenuSearchResult(menuSearchResults[activeMenuResultIndex])
    }

    if (event.key === "Escape") {
      event.preventDefault()
      setIsMenuSearchFocused(false)
    }
  }

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen().catch(err => {})
        setIsFullscreen(true)
      }
    } else {
      if (document.exitFullscreen) {
        if (document.fullscreenElement) {
          document.exitFullscreen().catch(err => {})
        }
        setIsFullscreen(false)
      }
    }
  }

  // Clears every piece of per-user selection state so the next login (even a
  // different user, on a shared/reception computer) starts clean instead of
  // inheriting this user's campus/session — sessionStorage covers
  // impersonation, but the regular (non-impersonating) selection lives in
  // localStorage and was previously never cleared on logout.
  const clearSessionState = () => {
    try {
      sessionStorage.removeItem("impersonationToken")
      sessionStorage.removeItem("currentBranchId")
      sessionStorage.removeItem("currentSessionId")
      localStorage.removeItem("currentBranchId")
      localStorage.removeItem("currentSessionId")
      localStorage.removeItem("branchesCache")
    } catch {
      /* ignore */
    }
  }

  const handleLogout = async (e: MouseEvent<HTMLDivElement>) => {
    e.preventDefault()
    try {
      await logout()
    } catch (error) {
      console.error("Logout failed:", error)
    } finally {
      clearSessionState()
      dispatch(resetUser())
    }
  }

  useEffect(() => {
    fetchProfile()
  }, [])
  // A new picture deserves a fresh chance to load, even if the previous one
  // failed. (Campus logos use ImageWithBasePath's own built-in fallback —
  // see its fullSrc prop — so only the avatar needs this here.)
  useEffect(() => setAvatarFailed(false), [profile?.profilePicture])
  const fetchProfile = async () => {
    try {
      const profileData = await getProfile()
      setProfile(profileData)
    } catch (err) {
      console.error("Error fetching profile:", err)
    }
  }

  return (
    <div className="header">
      {/* Logo */}
      <div
        className="header-left active"
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <Link to={routes.adminDashboard} className="logo logo-normal">
          <ImageWithBasePath
            src="assets/img/icons/logo.png"
            fullSrc={currentBranch?.logoUrl || undefined}
            alt={currentBranch?.name || "Logo"}
            className="img-fluid"
            style={{ maxHeight: 72, maxWidth: 200, objectFit: "contain" }}
          />
        </Link>
        <Link to={routes.adminDashboard} className="logo-small">
          <ImageWithBasePath
            src="assets/img/icons/logo.png"
            fullSrc={currentBranch?.logoUrl || undefined}
            alt={currentBranch?.name || "Logo"}
            className="img-fluid"
            style={{ maxHeight: 50, maxWidth: 60, objectFit: "contain" }}
          />
        </Link>
        <Link to={routes.adminDashboard} className="dark-logo">
          <ImageWithBasePath
            src="assets/img/icons/logo.png"
            fullSrc={currentBranch?.logoUrl || undefined}
            alt={currentBranch?.name || "Logo"}
            className="img-fluid"
            style={{ maxHeight: 72, maxWidth: 200, objectFit: "contain" }}
          />
        </Link>
        <Link id="toggle_btn" to="#" onClick={handleToggleMiniSidebar}>
          <i className="ti ti-menu-deep" />
        </Link>
      </div>
      {/* /Logo */}

      <Link
        id="mobile_btn"
        className="mobile_btn"
        to="#sidebar"
        onClick={toggleMobileSidebar}
      >
        <span className="bar-icon">
          <span />
          <span />
          <span />
        </span>
      </Link>

      <div className="d-flex justify-content-end align-items-center mt-2">
        <div className="header-user">
          <div className="nav user-menu">
            <div className="d-flex align-items-center">
              <div className="header-context-controls">
                <div
                  className="menu-search-wrapper me-2 d-none d-md-block"
                  ref={menuSearchRef}
                >
                  <div className="menu-search-input-wrap">
                    <i className="ti ti-search" />
                    <input
                      type="search"
                      className="menu-search-input"
                      placeholder="Search menus"
                      value={menuSearch}
                      ref={menuSearchInputRef}
                      aria-label="Search menus"
                      aria-controls="menu-search-results"
                      onChange={event => setMenuSearch(event.target.value)}
                      onFocus={() => setIsMenuSearchFocused(true)}
                      onKeyDown={handleMenuSearchKeyDown}
                    />
                    {menuSearch && (
                      <button
                        type="button"
                        className="menu-search-clear"
                        aria-label="Clear menu search"
                        onClick={() => {
                          setMenuSearch("")
                          setActiveMenuResultIndex(0)
                        }}
                      >
                        <i className="ti ti-x" />
                      </button>
                    )}
                  </div>
                  {isMenuSearchOpen && (
                    <div
                      className="menu-search-dropdown"
                      id="menu-search-results"
                  >
                    {menuSearchResults.length > 0 ? (
                      menuSearchResults.map((item, index) => {
                        const bookmarked = isMenuBookmarked(item)

                        return (
                          <div
                            key={`${item.link}-${item.path.join("-")}`}
                            role="button"
                            tabIndex={-1}
                            className={`menu-search-result ${
                              index === activeMenuResultIndex ? "active" : ""
                            }`}
                            onMouseEnter={() => setActiveMenuResultIndex(index)}
                            onMouseDown={event => {
                              event.preventDefault()
                              openMenuSearchResult(item)
                            }}
                          >
                            <span className="menu-search-result-icon">
                              <i className={item.icon || "ti ti-point"} />
                            </span>
                            <span className="menu-search-result-copy">
                              <span className="menu-search-result-title">
                                {item.label}
                              </span>
                              <span className="menu-search-result-path">
                                {item.path.join(" > ")}
                              </span>
                            </span>
                            <button
                              type="button"
                              className={`menu-search-star ${
                                bookmarked ? "active" : ""
                              }`}
                              aria-label={
                                bookmarked
                                  ? `Remove ${item.label} from bookmarks`
                                  : `Save ${item.label} to bookmarks`
                              }
                              onMouseDown={event => {
                                event.preventDefault()
                                event.stopPropagation()
                                toggleMenuBookmark(item)
                              }}
                            >
                              <i
                                className={
                                  bookmarked
                                    ? "ti ti-star-filled"
                                    : "ti ti-star"
                                }
                              />
                            </button>
                          </div>
                        )
                      })
                    ) : (
                      <div className="menu-search-empty">
                        No matching menus
                      </div>
                    )}
                    </div>
                  )}
                </div>
                {/* Sessions Dropdown */}
                <div className="dropdown me-2">
                  <div
                    className="btn btn-outline-light fw-normal bg-white d-flex align-items-center p-2"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    style={{ width: 170, flexShrink: 0 }}
                  >
                    <i className="ti ti-calendar-due me-1" style={{ flexShrink: 0 }} />
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {currentSession?.title}
                    </span>
                  </div>

                  <div
                    className="dropdown-menu dropdown-menu-right"
                    style={{ zIndex: 9999 }}
                  >
                    {sessions.map(session => (
                      <Link
                        key={session._id}
                        to="#"
                        className={`dropdown-item d-flex align-items-center ${
                          currentSession?._id === session._id
                            ? "active text-light"
                            : ""
                        }`}
                        onClick={e => {
                          e.preventDefault()
                          dispatch(setCurrentSession(session))
                        }}
                      >
                        {session.title}
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Branch: badge when 1 branch, dropdown when 2+. Branches
                    load async on mount (and the endpoint can be slow) — keep
                    the slot's shape while waiting instead of it just
                    vanishing, which reads as "the campus dropdown is gone". */}
                {branches.length === 0 && (
                  <div
                    className="btn btn-outline-light fw-normal bg-white d-flex align-items-center p-2 me-2"
                    style={{ cursor: "default", width: 170, flexShrink: 0, opacity: 0.6 }}
                  >
                    <i className="ti ti-building me-1" style={{ flexShrink: 0 }} />
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Loading...
                    </span>
                  </div>
                )}
                {branches.length === 1 && (
                  <div
                    className="btn btn-outline-light fw-normal bg-white d-flex align-items-center p-2 me-2"
                    style={{ cursor: "default", width: 170, flexShrink: 0 }}
                  >
                    <i className="ti ti-building me-1" style={{ flexShrink: 0 }} />
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {currentBranch?.name || branches[0]?.name}
                    </span>
                  </div>
                )}
                {branches.length > 1 && (
                  <div className="dropdown me-2">
                    <div
                      className="btn btn-outline-light fw-normal bg-white d-flex align-items-center p-2"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                      style={{ width: 170, flexShrink: 0 }}
                    >
                      <i className="ti ti-building me-1" style={{ flexShrink: 0 }} />
                      <span
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {currentBranch?.name || branches[0]?.name}
                      </span>
                    </div>
                    <div className="dropdown-menu dropdown-menu-right" style={{ zIndex: 9999 }}>
                      {branches.map(branch => (
                        <Link
                          key={branch._id}
                          to="#"
                          className={`dropdown-item d-flex align-items-center ${currentBranch?._id === branch._id ? "active text-light" : ""}`}
                          // Reload so every page refetches its data scoped to the newly
                          // selected branch (the apiClient reads the persisted branch).
                          onClick={e => { e.preventDefault(); dispatch(setCurrentBranch(branch)); window.location.reload() }}
                        >
                          {branch.campusNo ? `${branch.campusNo} — ` : ""}{branch.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Bookmarks Dropdown */}
              <div className="pe-1 d-none d-lg-block">
                <div className="dropdown">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white btn-icon me-1"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <i className="ti ti-bookmarks" />
                  </Link>
                  <div className="dropdown-menu dropdown-menu-right border shadow-sm dropdown-md bookmarks-dropdown-menu">
                    <div className="p-3 border-bottom">
                      <h5>Bookmarks</h5>
                    </div>
                    <div className="p-3">
                      {bookmarkedMenus.length > 0 ? (
                        <div className="row gx-2">
                          {bookmarkedMenus.map(item => (
                            <div className="col-6" key={item.link}>
                              <div className="bookmark-card-wrap">
                                <Link
                                  to={item.link}
                                  className="bookmark-card"
                                >
                                  <span className="bookmark-card-icon">
                                    <i className={item.icon || "ti ti-bookmark"} />
                                  </span>
                                  <span className="bookmark-card-title">
                                    {item.label}
                                  </span>
                                  <span className="bookmark-card-path">
                                    {item.path.join(" > ")}
                                  </span>
                                </Link>
                                <button
                                  type="button"
                                  className="bookmark-card-remove"
                                  aria-label={`Remove ${item.label} bookmark`}
                                  onClick={() => removeMenuBookmark(item.link)}
                                >
                                  <i className="ti ti-x" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="bookmark-empty">
                          <span>
                            <i className="ti ti-star" />
                          </span>
                          <p>Star menus from search to pin them here.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Dark Mode Toggle */}
              <div className="pe-1 d-none d-lg-block">
                {!location.pathname.includes("layout-dark") && (
                  <Link
                    onClick={handleToggleClick}
                    to="#"
                    id="dark-mode-toggle"
                    className="dark-mode-toggle activate btn btn-outline-light bg-white btn-icon me-1"
                  >
                    <i
                      className={
                        dataTheme === "default_data_theme"
                          ? "ti ti-moon"
                          : "ti ti-brightness-up"
                      }
                    />
                  </Link>
                )}
              </div>

              {/* Fullscreen Toggle */}
              <div className="pe-1 d-none d-lg-block">
                <Link
                  onClick={toggleFullscreen}
                  to="#"
                  className="btn btn-outline-light bg-white btn-icon me-1"
                  id="btnFullscreen"
                >
                  <i className="ti ti-maximize" />
                </Link>
              </div>

              {/* User Profile Dropdown */}
              <div className="dropdown ms-1">
                <Link
                  to="#"
                  className="dropdown-toggle d-flex align-items-center"
                  data-bs-toggle="dropdown"
                >
                  <span className="avatar avatar-md rounded">
                  {profile?.profilePicture && !avatarFailed ? (
                        <img
                          src={profile.profilePicture}
                          alt="Profile"
                          className="img-fluid rounded-circle"
                          onError={() => setAvatarFailed(true)}
                        />
                      ) : (
                        <span
                          className="d-flex align-items-center justify-content-center w-100 h-100 rounded-circle bg-primary text-white"
                          style={{ fontSize: 12, fontWeight: 600 }}
                        >
                          {`${profile?.firstName?.[0] || ""}${profile?.lastName?.[0] || ""}`.toUpperCase() || "U"}
                        </span>
                      )}
                  </span>
                </Link>
                <div className="dropdown-menu">
                  <div className="d-block">
                    <div className="d-flex align-items-center p-2">
                      <span className="avatar avatar-md me-2 online avatar-rounded">
                      {profile?.profilePicture && !avatarFailed ? (
                        <img
                          src={profile.profilePicture}
                          alt="Profile"
                          className="img-fluid rounded-circle"
                          onError={() => setAvatarFailed(true)}
                        />
                      ) : (
                        <span
                          className="d-flex align-items-center justify-content-center w-100 h-100 rounded-circle bg-primary text-white"
                          style={{ fontSize: 12, fontWeight: 600 }}
                        >
                          {`${profile?.firstName?.[0] || ""}${profile?.lastName?.[0] || ""}`.toUpperCase() || "U"}
                        </span>
                      )}
                      </span>
                      <div>
                        <h6>{`${profile?.firstName} ${profile?.lastName}`}</h6>
                        <p className="text-primary mb-0">{User.role.name}</p>
                      </div>
                    </div>
                    <hr className="m-0" />
                    <Link
                      className="dropdown-item d-inline-flex align-items-center p-2"
                      to={routes.profile}
                    >
                      <i className="ti ti-user-circle me-2" />
                      My Profile
                    </Link>
                    {/* <Link
                      className="dropdown-item d-inline-flex align-items-center p-2"
                      to={routes.profilesettings}
                    >
                      <i className="ti ti-settings me-2" />
                      Settings
                    </Link> */}
                    <hr className="m-0" />
                    <div
                      className="dropdown-item d-inline-flex align-items-center p-2"
                      style={{ cursor: "pointer" }}
                      onClick={handleLogout}
                    >
                      <i className="ti ti-login me-2" />
                      Logout
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className="dropdown mobile-user-menu">
        <Link
          to="#"
          className="nav-link dropdown-toggle"
          data-bs-toggle="dropdown"
          aria-expanded="false"
        >
          <i className="fa fa-ellipsis-v" />
        </Link>
        <div className="dropdown-menu dropdown-menu-end">
          <Link className="dropdown-item" to={routes.profile}>
            My Profile
          </Link>
          {/* <Link className="dropdown-item" to={routes.profilesettings}>
            Settings
          </Link> */}
          <div className="dropdown-item cursor-pointer" onClick={handleLogout}>
            Logout
          </div>
        </div>
      </div>
      {/* /Mobile Menu */}
    </div>
  )
}

export default Header
